/**
 * <faq-accordion> — progressive enhancement for the FAQ section.
 *
 * Native <details> snaps open instantly, which reads as a jarring layout jump.
 * This intercepts the toggle and animates the answer's height open/closed with
 * the Web Animations API so the reveal feels intentional. Pure enhancement:
 * with JS disabled (or reduced-motion preferred) the native instant toggle
 * still works, so the FAQ is never broken.
 */

const DURATION = 280;

class FaqAccordion extends HTMLElement {
  #reduceMotion = false;

  connectedCallback() {
    this.#reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelectorAll('details.faq__item').forEach((details) => {
      const summary = details.querySelector('summary');
      if (summary) summary.addEventListener('click', (e) => this.#onToggle(e, details));
    });
  }

  #onToggle(event, details) {
    if (this.#reduceMotion) return; // let the browser toggle natively
    const content = details.querySelector('.faq__a');
    if (!content) return;
    event.preventDefault();
    if (details.dataset.animating) return;
    if (details.open) this.#collapse(details, content);
    else this.#expand(details, content);
  }

  #expand(details, content) {
    details.dataset.animating = '1';
    details.open = true; // render content + rotate chevron immediately
    this.#animate(details, content, content.scrollHeight, 0, () => {
      delete details.dataset.animating;
    });
  }

  #collapse(details, content) {
    details.dataset.animating = '1';
    this.#animate(details, content, 0, content.scrollHeight, () => {
      details.open = false; // hide content once the close animation finishes
      delete details.dataset.animating;
    });
  }

  #animate(details, content, toPx, fromPx, done) {
    content.style.overflow = 'hidden';
    const opening = toPx >= fromPx;
    const anim = content.animate(
      [
        { height: `${fromPx}px`, opacity: opening ? 0 : 1 },
        { height: `${toPx}px`, opacity: opening ? 1 : 0 },
      ],
      { duration: DURATION, easing: 'ease' }
    );
    anim.onfinish = anim.oncancel = () => {
      content.style.removeProperty('overflow');
      done();
    };
  }
}

if (!customElements.get('faq-accordion')) {
  customElements.define('faq-accordion', FaqAccordion);
}
